// src/components/ResultsScreen.tsx
import React, { useMemo, useState } from "react";
import {
  Package,
  CheckCircle,
  User,
  Mail,
  Phone,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { useSubmitQuizMutation } from "../../../app/api/roadmapApi";

// --- Types ---
interface Tier {
  price: number;
  units: number;
}
interface Result {
  totalRevenue: string;
  tiers: Tier[];
  timeline?: string;
}

type Props = {
  result: Result;
  skill: string;
  onFirstNameChange: (name: string) => void;
  onEmailChange: (email: string) => void;
  onWhatsappChange: (whatsapp: string) => void;
  onSubmit?: () => void;
  firstName: string;
  email: string;
  whatsapp: string;
};

// --- Helpers ---
const currency = (n: number) =>
  `₦${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// --- Component ---
const ResultsScreen: React.FC<Props> = ({
  result,
  skill,
  onFirstNameChange,
  onEmailChange,
  onWhatsappChange,
  onSubmit,
  firstName,
  email,
  whatsapp,
}) => {
  const [submitQuiz, { isLoading }] = useSubmitQuizMutation();
  const [success, setSuccess] = useState(false);
  const [remoteLink, setRemoteLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isFormValid = useMemo(
    () =>
      firstName.trim().length > 1 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
      whatsapp.trim().replace(/\D/g, "").length >= 9,
    [firstName, email, whatsapp]
  );

  const handleSubmit = async () => {
    setError(null);
    if (!isFormValid) {
      setError("Please complete name, email and WhatsApp number.");
      return;
    }

    try {
      const payload = {
        skill,
        monthlyGoal: result.totalRevenue,
        firstName,
        email,
        whatsapp,
        source: { name: "quiz_frontend" },
      };

      const res = await submitQuiz(payload).unwrap();
      if (res?.ok) {
        setRemoteLink(res.link || null);
        setSuccess(true);
        // keep backward compatibility: call parent onSubmit if supplied
        try {
          onSubmit && onSubmit();
        } catch (e) {
          // ignore parent errors
        }
      } else {
        setError("Could not save your blueprint. Try again.");
      }
    } catch (err: any) {
      setError(err?.data?.message || err?.message || "Submission failed");
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-brandNavy flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <CheckCircle className="w-20 h-20 text-brandRed mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-brandNavy mb-2">
            You're set — blueprint sent!
          </h2>
          <p className="text-sm text-mutedGray mb-4">
            We emailed your action plan and saved a copy to your roadmap.
          </p>

          {remoteLink && (
            <a
              href={remoteLink}
              target="_blank"
              rel="noreferrer"
              className="inline-block bg-brandRed text-white px-6 py-3 rounded-lg font-semibold shadow-md mb-4"
            >
              Open My Roadmap
            </a>
          )}

          <div className="text-sm text-mutedGray mt-4">
            You'll get a WhatsApp message shortly. If you don't receive it,
            check spam or try again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brandNavy via-brandLightNavy to-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 bg-brandRed/10 text-brandRed px-4 py-2 rounded-full mx-auto mb-4">
            <Sparkles className="w-5 h-5" />
            <span className="font-semibold">Blueprint Ready</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-2">
            Your <span className="text-green-300">{result.totalRevenue}</span>{" "}
            Revenue Blueprint
          </h1>
          <p className="text-sm text-white/80 max-w-2xl mx-auto">
            Based on your {skill} expertise, here's a 3-tier product ladder and
            monthly targets to reach {result.totalRevenue}.
          </p>
        </div>

        {/* tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {result.tiers.map((tier, i) => (
            <div
              key={i}
              className="rounded-2xl overflow-hidden shadow-xl border border-white/6 bg-gradient-to-br from-white/5 to-white/2 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="text-2xl font-bold text-white">
                  Tier {i + 1}
                </div>
                <div className="text-sm text-white/80">{tier.units}/mo</div>
              </div>

              <div className="text-sm text-white/80 mb-2">Price</div>
              <div className="text-2xl font-extrabold text-green-300 mb-4">
                {currency(tier.price)}
              </div>

              <div className="text-xs text-white/80">Monthly revenue</div>
              <div className="text-2xl font-extrabold text-yellow-300">
                {currency(tier.price * tier.units)}
              </div>
            </div>
          ))}
        </div>

        {/* value + CTA */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="bg-white/5 rounded-2xl p-6 border border-white/6">
            <h3 className="text-lg font-bold text-white mb-3">
              Why this works
            </h3>
            <ul className="text-white/80 space-y-3">
              <li>• Diversified pricing to capture multiple buyer segments</li>
              <li>• Optimized for your market and skillset</li>
              <li>• Quick 30-day timeline to validate & sell</li>
            </ul>
          </div>

          <aside className="bg-gradient-to-br from-white/6 to-white/4 rounded-2xl p-6 border border-white/6">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-brandRed rounded-md p-3">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-sm text-mutedGray">Get your full plan</div>
                <div className="text-lg font-bold text-white">
                  Implementation Guide
                </div>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit();
              }}
              className="space-y-3"
            >
              <label className="sr-only" htmlFor="rs-firstname">
                First name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-3 w-4 h-4 text-mutedGray" />
                <input
                  id="rs-firstname"
                  type="text"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => onFirstNameChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white text-brandNavy placeholder:text-mutedGray focus:outline-none focus:ring-2 focus:ring-brandRed transition"
                />
              </div>

              <div className="relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-mutedGray" />
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white text-brandNavy placeholder:text-mutedGray focus:outline-none focus:ring-2 focus:ring-brandRed transition"
                />
              </div>

              <div className="relative">
                <Phone className="absolute left-3 top-3 w-4 h-4 text-mutedGray" />
                <input
                  type="tel"
                  placeholder="WhatsApp number (e.g., +2348012345678)"
                  value={whatsapp}
                  onChange={(e) => onWhatsappChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white text-brandNavy placeholder:text-mutedGray focus:outline-none focus:ring-2 focus:ring-brandRed transition"
                />
              </div>

              {error && <div className="text-sm text-red-400">{error}</div>}

              <button
                type="submit"
                disabled={!isFormValid || isLoading}
                className="w-full inline-flex items-center justify-center gap-3 py-3 rounded-lg bg-brandRed text-white font-bold shadow-md disabled:opacity-50"
                aria-busy={isLoading}
              >
                {isLoading ? "Sending…" : "Send Me The Complete Plan"}
                <ArrowRight className="w-4 h-4" />
              </button>

              <div className="text-xs text-mutedGray mt-2">
                Free resource • No spam • Instant delivery to your email &
                WhatsApp
              </div>
            </form>
          </aside>
        </div>

        {/* trust */}
        <div className="text-center mt-8">
          <div className="inline-flex items-center gap-6 bg-white/6 px-6 py-3 rounded-full">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-300" />{" "}
              <span className="text-white text-sm">100% Free</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-300" />{" "}
              <span className="text-white text-sm">No Spam</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-300" />{" "}
              <span className="text-white text-sm">Instant Access</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsScreen;
